import React, { useEffect, useState } from "react"
import { Form, Formik, FormikHelpers, FormikProps } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import moment from "moment"
import MyModal from "@common-utils/MyModal"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import MySelectGetAllDataFromServer from "@common-utils/fields/MySelectGetAllDataFromServer"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT } from "../../../endpoints"
import {
     IPersonnelSortiePreviewModel,
     IPersonnelSortieRequestModel,
     personnelHasRespGroupRequest,
     personnelSortieDesEffectifsPreviewRequest,
     personnelSortieDesEffectifsRequest,
} from "../core/_requests"

interface IProps {
     personnelId: number
     personnelNom: string
     onClose: () => void
     onDone: (dateSortie: string) => void
}

interface IForm {
     dateSortie?: string
     responsable?: number | null
     remplacantHierarchique?: number | null
}

const SortieDesEffectifsModal = ({ personnelId, personnelNom, onClose, onDone }: IProps) => {
     const [preview, setPreview] = useState<IPersonnelSortiePreviewModel | null>(null)
     const [loadError, setLoadError] = useState<string | null>(null)
     // undefined = pas encore choisi, null = vérification en cours, true/false = résultat du check groupe resp
     const [respGroupOk, setRespGroupOk] = useState<boolean | null | undefined>(undefined)

     useEffect(() => {
          personnelSortieDesEffectifsPreviewRequest(personnelId)
               .then(r => setPreview(r.data))
               .catch((e: AxiosError) => setLoadError((e.response?.data as IHttpErrorResponseModel)?.detail ?? "Erreur lors du chargement."))
     }, [personnelId])

     // Périmètre repris par le nouveau responsable
     const nbConsultants = preview?.nbConsultants ?? 0
     const nbPropals = preview?.nbStandalonePropals ?? 0
     const nbAnnonces = preview?.nbAnnonces ?? 0
     const nbViviers = preview?.nbViviers ?? 0
     const hasPerimetre = nbConsultants + nbPropals + nbAnnonces + nbViviers > 0
     // Le groupe resp n'est nécessaire que s'il y a des fichiers Drive (consultants ou propositions)
     const needsRespGroup = nbConsultants + nbPropals > 0
     const showRemplacant = !!preview && preview.hasSubordinates
     const remplacantRequired = !!preview && preview.hasSubordinates && preview.isRoot

     const initialValues: IForm = { dateSortie: moment().format("YYYY-MM-DD") }

     const schema = Yup.object().shape({
          dateSortie: Yup.string().required().label(`"Date de sortie"`),
          responsable: hasPerimetre ? Yup.number().required().label(`"Nouveau responsable"`) : Yup.number().nullable(),
          remplacantHierarchique: remplacantRequired ? Yup.number().required().label(`"Remplaçant hiérarchique"`) : Yup.number().nullable(),
     })

     // Vérifie (proactivement) que le responsable choisi a bien un groupe resp GWS (si nécessaire)
     function onResponsableChange(val: number | null, helpers: FormikProps<IForm>) {
          helpers.setFieldValue("responsable", val)
          if (!val || !needsRespGroup) {
               setRespGroupOk(undefined)
               return
          }
          setRespGroupOk(null) // vérification en cours
          personnelHasRespGroupRequest(val)
               .then(r => setRespGroupOk(r.data.hasRespGroup))
               .catch(() => setRespGroupOk(undefined))
     }

     // Bloque la soumission tant que le responsable requis n'a pas (ou pas encore confirmé) de groupe resp
     const responsableBlocked = needsRespGroup && (respGroupOk === false || respGroupOk === null)

     function handleSubmit(values: IForm, helpers: FormikHelpers<IForm>) {
          helpers.setStatus(null)
          const payload: IPersonnelSortieRequestModel = {
               dateSortie: values.dateSortie as string,
               responsable: hasPerimetre ? values.responsable : null,
               remplacantHierarchique: showRemplacant ? values.remplacantHierarchique : null,
          }
          personnelSortieDesEffectifsRequest(personnelId, payload)
               .then(r => {
                    const warnings: string[] = r?.data?.warnings ?? []
                    toast.success(`${personnelNom} a été sorti des effectifs.`)
                    warnings.forEach(w => toast.warning(w, { autoClose: false }))
                    onDone(payload.dateSortie)
                    onClose()
               })
               .catch((e: AxiosError) => {
                    const error = e.response?.data as IHttpErrorResponseModel
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
     }

     // Récapitulatif de ce qui sera transféré
     const perimetreLabel = [
          nbConsultants ? `${nbConsultants} consultant(s)` : null,
          nbPropals ? `${nbPropals} proposition(s)` : null,
          nbAnnonces ? `${nbAnnonces} annonce(s)` : null,
          nbViviers ? `${nbViviers} vivier(s)` : null,
     ]
          .filter(Boolean)
          .join(", ")

     return (
          <MyModal show={true} onCloseCallback={onClose} title={<>Sortir des effectifs — {personnelNom}</>}>
               {!preview && !loadError && (
                    <div className="text-center p-4">
                         <MySimpleSpinner />
                    </div>
               )}
               {loadError && <MyAlert type={"danger"}>{loadError}</MyAlert>}

               {preview && (
                    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                         {helpers => (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   <MyAlert type={"warning"} classNames={"mb-4"} isDismissible={false}>
                                        Cette action retire le collaborateur des effectifs, réaffecte son périmètre, le retire des équipes et de la
                                        hiérarchie, et coupe son accès à l'application. Le compte Google n'est pas touché.
                                   </MyAlert>

                                   {/* Date de sortie */}
                                   <div className="mb-4">
                                        <label className="fw-bold fs-6 required mb-1">Date de sortie</label>
                                        <MyDateDayMonthYearField
                                             name={"dateSortie"}
                                             value={helpers.values.dateSortie}
                                             isInvalid={!!helpers.errors.dateSortie}
                                             onChange={date => helpers.setFieldValue("dateSortie", date)}
                                             placeholder={"Date de sortie"}
                                        />
                                        {helpers.errors.dateSortie && <div className={"text-danger"}>{helpers.errors.dateSortie}</div>}
                                   </div>

                                   {hasPerimetre && (
                                        <div className="mb-4">
                                             <label className="fw-bold fs-6 required mb-1">
                                                  Nouveau responsable<span className="text-muted ms-1">({perimetreLabel})</span>
                                             </label>
                                             <MySelectGetAllDataFromServer
                                                  name={"responsable"}
                                                  value={helpers.values.responsable}
                                                  method={"GET"}
                                                  isInvalid={!!helpers.errors.responsable || respGroupOk === false}
                                                  onChange={val => onResponsableChange(val, helpers)}
                                                  url={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                             />
                                             {helpers.errors.responsable && <div className={"text-danger"}>{helpers.errors.responsable}</div>}
                                             {respGroupOk === null && <div className="text-muted small">Vérification du groupe resp…</div>}
                                             {respGroupOk === false && (
                                                  <div className="text-danger">Ce responsable n'a pas de groupe resp GWS : choisissez-en un autre.</div>
                                             )}
                                        </div>
                                   )}

                                   {showRemplacant && (
                                        <div className="mb-4">
                                             <label className={`fw-bold fs-6 mb-1 ${remplacantRequired ? "required" : ""}`}>
                                                  Remplaçant hiérarchique
                                                  <span className="text-muted ms-1">
                                                       {remplacantRequired
                                                            ? "(obligatoire : ce collaborateur n'a pas de N+1)"
                                                            : "(optionnel — sinon les subordonnés sont rattachés au N+1)"}
                                                  </span>
                                             </label>
                                             <MySelectGetAllDataFromServer
                                                  name={"remplacantHierarchique"}
                                                  value={helpers.values.remplacantHierarchique}
                                                  method={"GET"}
                                                  isInvalid={!!helpers.errors.remplacantHierarchique}
                                                  onChange={val => helpers.setFieldValue("remplacantHierarchique", val)}
                                                  url={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                             />
                                             {helpers.errors.remplacantHierarchique && <div className={"text-danger"}>{helpers.errors.remplacantHierarchique}</div>}
                                        </div>
                                   )}

                                   <div className="d-flex justify-content-end mt-5">
                                        <button
                                             type={"button"}
                                             className={"btn btn-outline btn-active-secondary btn-sm me-2"}
                                             onClick={onClose}
                                             disabled={helpers.isSubmitting}
                                        >
                                             Annuler
                                        </button>
                                        <button type={"submit"} className={"btn btn-warning btn-sm"} disabled={helpers.isSubmitting || responsableBlocked}>
                                             {!helpers.isSubmitting ? <>Confirmer la sortie</> : <MySimpleSpinner size={"sm"} />}
                                        </button>
                                   </div>
                              </Form>
                         )}
                    </Formik>
               )}
          </MyModal>
     )
}

export default SortieDesEffectifsModal
